import { memo, useEffect, useRef } from 'react';
import {
  SOA_REASONS,
  SOA_STATUSES,
  SOA_STATUS_LABELS,
  type SoaControlRow,
  type SoaStatus,
} from '@grc/types';
import {
  Badge,
  Button,
  Field,
  Input,
  Select,
  Textarea,
  formatDateTime,
} from '../../components/ui.js';
import {
  REASON_PLACEHOLDERS,
  isDirty,
  linkText,
  reasonsSummary,
  setReasonDetail,
  toggleReason,
  ukToday,
  valuesOf,
  type SoaEdit,
  type SoaLinkSummary,
} from './soaModel.js';

export interface MemberOption {
  id: string;
  name: string;
}

/** Columns of the compact row — the editor row spans them all. */
export const SOA_COLUMN_COUNT = 7;

const APPLICABLE_STATUSES: ReadonlyArray<SoaStatus> = SOA_STATUSES.filter(
  (status) => status !== 'not_applicable',
);

/** Issue paths rendered under their own field in the editor. */
const FIELD_ERRORS: ReadonlySet<string> = new Set(['reasons', 'justification', 'ownerUserId']);

export interface SoaRowProps {
  row: SoaControlRow;
  /** This row's unsaved changes (undefined when clean). */
  edit: SoaEdit | undefined;
  expanded: boolean;
  canWrite: boolean;
  saving: boolean;
  /** Inline issues from the last save attempt, field → message. */
  errors: Record<string, string> | undefined;
  /** Evidence / response status of the chosen assessment for this control. */
  link: SoaLinkSummary | undefined;
  hasAssessment: boolean;
  linksReady: boolean;
  members: ReadonlyArray<MemberOption>;
  onChange: (clauseId: string, patch: SoaEdit) => void;
  onToggle: (clauseId: string, open?: boolean) => void;
  onSave: (clauseId: string) => void;
  onDiscard: (clauseId: string) => void;
  onResetRequest: (clauseId: string) => void;
}

/**
 * One control of the Statement of Applicability: a compact row (Applicable
 * checkbox and Status select inline) plus, when expanded, a full-width editor
 * row. Memoised so only the row being edited re-renders on each keystroke —
 * every callback prop must be stable (spec 3.6).
 */
function SoaRowInner({
  row,
  edit,
  expanded,
  canWrite,
  saving,
  errors,
  link,
  hasAssessment,
  linksReady,
  members,
  onChange,
  onToggle,
  onSave,
  onDiscard,
  onResetRequest,
}: SoaRowProps) {
  const values = valuesOf(row, edit);
  const dirty = isDirty(edit);
  const editorId = `soa-editor-${row.clauseId}`;

  // Unticking Applicable focuses the Justification field, which is required
  // for an excluded control; the editor may need to open first.
  const justificationId = useRef<string | null>(null);
  const focusPending = useRef(false);
  const focusJustification = () => {
    if (justificationId.current) document.getElementById(justificationId.current)?.focus();
  };
  useEffect(() => {
    if (expanded && focusPending.current) {
      focusPending.current = false;
      focusJustification();
    }
  }, [expanded]);

  const change = (patch: SoaEdit) => onChange(row.clauseId, patch);

  const setApplicable = (applicable: boolean) => {
    if (applicable) {
      // Re-ticking undoes exactly what unticking did: an `undefined` patch value
      // drops the key, so the emptied reasons, the blanked seed justification and
      // the status come back from the server row. A control the server itself
      // excludes starts again at "planned", as the server would coerce it.
      change({
        applicable: true,
        ...(values.implementationStatus === 'not_applicable'
          ? {
              implementationStatus:
                row.implementationStatus === 'not_applicable' ? ('planned' as const) : undefined,
            }
          : {}),
        ...(edit?.reasons !== undefined && edit.reasons.length === 0 ? { reasons: undefined } : {}),
        ...(row.seeded && edit?.justification === '' ? { justification: undefined } : {}),
      });
      return;
    }
    // An excluded control has no selection reasons (the export prints "Not
    // applicable") and its status is always not_applicable. A seed justification
    // explains why the control matters, not why it is excluded, so it is blanked
    // unless the person has already written their own.
    const patch: SoaEdit = { applicable: false, reasons: [], implementationStatus: 'not_applicable' };
    if (row.seeded && edit?.justification === undefined) patch.justification = '';
    change(patch);
    if (expanded) {
      focusJustification();
    } else {
      focusPending.current = true;
      onToggle(row.clauseId, true);
    }
  };

  const ownerName =
    values.ownerUserId === row.ownerUserId
      ? row.ownerName
      : (members.find((member) => member.id === values.ownerUserId)?.name ?? null);
  // Only once the member list is in: an owner who has since left the organisation.
  const formerOwner =
    members.length > 0 &&
    row.ownerUserId !== null &&
    !members.some((member) => member.id === row.ownerUserId);
  // Issues no field owns (e.g. a stale clause after a standard re-import) are shown at the top.
  const otherErrors = Object.entries(errors ?? {}).filter(([field]) => !FIELD_ERRORS.has(field));
  const statusOptions = values.applicable ? APPLICABLE_STATUSES : (['not_applicable'] as const);

  return (
    <>
      <tr className={`soa-row${dirty ? ' dirty' : ''}${expanded ? ' expanded' : ''}`}>
        <td className="soa-col-control">
          <span className="soa-num">{row.clauseNumber}</span>
          <span className="soa-title" title={row.description || undefined}>
            {row.title}
          </span>
          {(row.seeded || dirty) && (
            <span className="soa-badges">
              {row.seeded && <Badge tone="info">best practice</Badge>}
              {dirty && <Badge tone="warning">Unsaved</Badge>}
            </span>
          )}
        </td>
        <td className="soa-col-check">
          <input
            type="checkbox"
            checked={values.applicable}
            disabled={!canWrite}
            aria-label={`Applicable: ${row.clauseNumber} ${row.title}`}
            onChange={(e) => setApplicable(e.target.checked)}
          />
        </td>
        <td className="soa-col-reasons">
          <span className="soa-muted">{reasonsSummary(values)}</span>
        </td>
        <td className="soa-col-status">
          {values.applicable ? (
            <Select
              aria-label={`Implementation status: ${row.clauseNumber} ${row.title}`}
              value={values.implementationStatus}
              disabled={!canWrite}
              onChange={(e) => change({ implementationStatus: e.target.value as SoaStatus })}
            >
              {APPLICABLE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {SOA_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          ) : (
            <Badge tone="neutral">{SOA_STATUS_LABELS.not_applicable}</Badge>
          )}
          {values.statusNote && <span className="soa-muted soa-note">{values.statusNote}</span>}
        </td>
        <td className="soa-col-owner">
          {ownerName ?? <span className="soa-muted">—</span>}
        </td>
        <td className="soa-col-evidence">
          <span className="soa-muted">{linkText(link, hasAssessment, linksReady)}</span>
        </td>
        <td className="soa-col-actions">
          <div className="soa-actions">
            {canWrite && dirty && (
              <Button size="sm" loading={saving} onClick={() => onSave(row.clauseId)}>
                Save
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              aria-expanded={expanded}
              aria-controls={expanded ? editorId : undefined}
              onClick={() => onToggle(row.clauseId)}
            >
              {expanded ? 'Close' : canWrite ? 'Edit' : 'Details'}
            </Button>
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="soa-editor" id={editorId}>
          <td colSpan={SOA_COLUMN_COUNT}>
            {row.description && <p className="soa-description">{row.description}</p>}
            {otherErrors.length > 0 && (
              <p className="field-error" role="alert">
                {otherErrors.map(([field, message]) => `${field}: ${message}`).join(' · ')}
              </p>
            )}
            <div className="soa-editor-grid">
              <div>
                <fieldset className="soa-reasons" disabled={!canWrite || !values.applicable}>
                  <legend>Reason(s) for selection</legend>
                  {!values.applicable && (
                    <p className="soa-muted">
                      An excluded control has no selection reasons — the export prints "Not
                      applicable".
                    </p>
                  )}
                  {SOA_REASONS.map((reason) => {
                    const entry = values.reasons.find((item) => item.reason === reason);
                    return (
                      <div key={reason} className="soa-reason">
                        <label>
                          <input
                            type="checkbox"
                            checked={!!entry}
                            onChange={(e) =>
                              change({ reasons: toggleReason(values.reasons, reason, e.target.checked) })
                            }
                          />
                          {reason}
                        </label>
                        {entry && (
                          <Input
                            aria-label={`${reason} detail`}
                            placeholder={REASON_PLACEHOLDERS[reason]}
                            maxLength={1000}
                            value={entry.detail}
                            readOnly={!canWrite}
                            onChange={(e) =>
                              change({ reasons: setReasonDetail(values.reasons, reason, e.target.value) })
                            }
                          />
                        )}
                      </div>
                    );
                  })}
                  {errors?.reasons && (
                    <span className="field-error" role="alert">
                      {errors.reasons}
                    </span>
                  )}
                </fieldset>

                <Field
                  label="Justification"
                  required={!values.applicable}
                  error={errors?.justification}
                  hint={
                    values.applicable
                      ? 'Why this control matters to the organisation'
                      : 'Why the control is excluded — required for an excluded control'
                  }
                >
                  {(props) => {
                    justificationId.current = props.id;
                    return (
                      <Textarea
                        {...props}
                        rows={4}
                        maxLength={5000}
                        value={values.justification}
                        readOnly={!canWrite}
                        onChange={(e) => change({ justification: e.target.value })}
                      />
                    );
                  }}
                </Field>

                <Field
                  label="Implementation details"
                  hint="Documents, tooling and practices — one point per line"
                >
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={6}
                      maxLength={5000}
                      value={values.implementation}
                      readOnly={!canWrite}
                      onChange={(e) => change({ implementation: e.target.value })}
                    />
                  )}
                </Field>
              </div>

              <div>
                <Field label="Implementation status">
                  {(props) => (
                    <Select
                      {...props}
                      value={values.implementationStatus}
                      disabled={!canWrite || !values.applicable}
                      onChange={(e) => change({ implementationStatus: e.target.value as SoaStatus })}
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {SOA_STATUS_LABELS[status]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field label="Status note" hint="Printed after the status, e.g. Partially implemented – note">
                  {(props) => (
                    <Input
                      {...props}
                      maxLength={5000}
                      placeholder={
                        values.applicable
                          ? 'e.g. Feed sources are in place; the review record is being drafted'
                          : `e.g. Exclusion re-confirmed ${ukToday()}`
                      }
                      value={values.statusNote}
                      readOnly={!canWrite}
                      onChange={(e) => change({ statusNote: e.target.value })}
                    />
                  )}
                </Field>

                <Field label="Control owner" error={errors?.ownerUserId}>
                  {(props) => (
                    <Select
                      {...props}
                      value={values.ownerUserId ?? ''}
                      disabled={!canWrite}
                      onChange={(e) => change({ ownerUserId: e.target.value || null })}
                    >
                      <option value="">No owner</option>
                      {formerOwner && (
                        <option value={row.ownerUserId!}>
                          {row.ownerName ?? 'Former member'} (former member)
                        </option>
                      )}
                      {members.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field
                  label="Evidence note"
                  hint="Manual evidence references, in addition to the evidence attached in the assessment"
                >
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={3}
                      maxLength={5000}
                      value={values.evidenceNote}
                      readOnly={!canWrite}
                      onChange={(e) => change({ evidenceNote: e.target.value })}
                    />
                  )}
                </Field>

                <div className="soa-auto-evidence">
                  <span className="field-label">Evidence from the assessment</span>
                  {!hasAssessment ? (
                    <p className="soa-muted">No assessment selected.</p>
                  ) : !linksReady ? (
                    <p className="soa-muted">Loading…</p>
                  ) : (
                    <>
                      <p className="soa-muted">Response status: {link?.responseStatus ?? 'No questions'}</p>
                      {link && link.evidenceFiles.length > 0 ? (
                        <ul className="soa-evidence-list">
                          {link.evidenceFiles.map((name) => (
                            <li key={name}>{name}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="soa-muted">No evidence attached</p>
                      )}
                    </>
                  )}
                </div>

                {row.updatedAt && (
                  <p className="soa-muted">
                    Last saved {formatDateTime(row.updatedAt)}
                    {row.updatedByName ? ` by ${row.updatedByName}` : ''}
                  </p>
                )}
              </div>
            </div>

            <div className="soa-editor-actions">
              {canWrite && row.seed && (
                <Button variant="ghost" size="sm" onClick={() => onResetRequest(row.clauseId)}>
                  Reset row to best practice
                </Button>
              )}
              {canWrite && dirty && (
                <Button variant="secondary" size="sm" onClick={() => onDiscard(row.clauseId)}>
                  Discard changes
                </Button>
              )}
              {canWrite && (
                <Button size="sm" loading={saving} disabled={!dirty} onClick={() => onSave(row.clauseId)}>
                  Save
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => onToggle(row.clauseId, false)}>
                Close
              </Button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export const SoaRow = memo(SoaRowInner);
